import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Box,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  School as SchoolIcon,
  Assessment as ReportIcon,
  Update as UpdateIcon,
  Sync as SyncIcon,
  Error as ErrorIcon,
  Settings as SystemIcon,
  Link as ConnectionIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const RecentActivity: React.FC = () => {
  const { data: activityData, isLoading, error } = useQuery({
    queryKey: ['recentActivity'],
    queryFn: async () => {
      const token = localStorage.getItem('token') || localStorage.getItem('emis_auth_token');
      const response = await axios.get(`${API_URL}/sis-integration/real-recent-activity/`, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      return response.data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <Typography color="error">Failed to load recent activity</Typography>
      </Box>
    );
  }

  const activities = activityData?.data?.activities || [];

  const getTimeChip = (time: string, status?: string) => {
    let color: 'success' | 'warning' | 'error' | 'default' = 'default';

    if (status === 'failed') {
      color = 'error';
    } else if (time.includes('hour')) {
      color = 'success';
    } else if (time.includes('1 day')) {
      color = 'warning';
    }

    return <Chip label={time} size="small" color={color} variant="outlined" />;
  };

  const getActivityIcon = (iconType: string, status?: string) => {
    const iconColor = status === 'failed' ? 'error' : 'primary';

    switch (iconType) {
      case 'upload':
        return <UploadIcon color={iconColor} />;
      case 'sync':
        return <SyncIcon color={iconColor} />;
      case 'assessment':
        return <ReportIcon color={iconColor} />;
      case 'update':
        return <UpdateIcon color={iconColor} />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'system':
        return <SystemIcon color="info" />;
      case 'connection':
        return <ConnectionIcon color="success" />;
      case 'setup':
        return <SystemIcon color="primary" />;
      default:
        return <SyncIcon color={iconColor} />;
    }
  };

  return (
    <Box sx={{ height: 300, overflow: 'auto' }}>
      <List>
        {activities.map((activity: any) => (
          <ListItem key={activity.id} divider>
            <ListItemIcon>
              {getActivityIcon(activity.icon_type, activity.status)}
            </ListItemIcon>
            <ListItemText
              primary={activity.message}
              secondary={activity.description}
            />
            <Box>
              {getTimeChip(activity.time, activity.status)}
            </Box>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default RecentActivity;